import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const HandEyeLinePlay = () => {
  const canvasRef    = useRef();
  const requestRef   = useRef();
  const navigate     = useNavigate();

  const [userPath,   setUserPath]   = useState([]);
  const [finishMsg,  setFinishMsg]  = useState(null);

  const ballRef            = useRef({ x: 0, y: 0, size: 12 });
  const startTimeRef       = useRef(null);
  const nextSampleRef      = useRef(0);
  const lastXRef           = useRef(0);
  const lastTimeRef        = useRef(Date.now());
  const endLineRef         = useRef(false);
  const finishedRef        = useRef(false);
  const innerLineCoordsRef = useRef([]);
  const outerLineCoordsRef = useRef([]);
  const endLineCoordsRef   = useRef([]);
  const gameStartedRef     = useRef(false);
  const axisCenterRef      = useRef({ h: 0, v: 0 });
  const calibratedRef      = useRef(false);
  const penaltyRef         = useRef(0);

  // ================= INIT =================
  useEffect(() => {
    init();

    calibrateJoystick().then(offsets => {
      axisCenterRef.current = offsets;
      calibratedRef.current = true;
    });

    requestRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(requestRef.current);
  }, []);

  const init = () => {
    const canvas = canvasRef.current;

    // ── KEY FIX: use innerWidth/innerHeight — this matches CSS 100vw/100vh exactly
    // window.screen.width/height includes OS taskbar & browser chrome → too large
    // → canvas coords go off the visible area at the top
    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;

    const w = canvas.width;
    const h = canvas.height;

    gameStartedRef.current = false;
    finishedRef.current    = false;
    endLineRef.current     = false;
    penaltyRef.current     = 0;
    lastTimeRef.current    = Date.now();

    ballRef.current = {
      x:    w * 0.1,
      y:    (0.4 * h + 0.5 * h) / 2,
      size: 10
    };

    lastXRef.current           = ballRef.current.x;
    innerLineCoordsRef.current = [];
    outerLineCoordsRef.current = [];
    endLineCoordsRef.current   = [];
    setUserPath([]);
  };

  // ================= CALIBRATION =================
  const calibrateJoystick = async (samples = 8) => {
    let accH = 0, accV = 0;
    for (let i = 0; i < samples; i++) {
      const gp = navigator.getGamepads()[0];
      if (gp) { accH += gp.axes[2] || 0; accV += gp.axes[1] || 0; }
      await new Promise(res => setTimeout(res, 20));
    }
    return { h: accH / samples, v: accV / samples };
  };

  const loop = () => { update(); draw(); requestRef.current = requestAnimationFrame(loop); };

  // ================= INPUT =================
  const getInput = () => {
    const gp = navigator.getGamepads()[0];
    if (!gp || !calibratedRef.current) return { dx: 0, dy: 0, active: false };

    let dx = (gp.axes[2] ?? 0) - axisCenterRef.current.h;
    let dy = (gp.axes[1] ?? 0) - axisCenterRef.current.v;

    const DZ = 0.25;
    if (Math.abs(dx) < DZ) dx = 0;
    if (Math.abs(dy) < DZ) dy = 0;
    dx = Math.max(-1, Math.min(1, dx));
    dy = Math.max(-1, Math.min(1, dy));

    return { dx, dy, active: Math.abs(dx) > 0 || Math.abs(dy) > 0 };
  };

  // ================= UPDATE =================
  const update = () => {
    if (finishedRef.current) return;

    const input  = getInput();
    const ball   = ballRef.current;
    const canvas = canvasRef.current;
    const now    = Date.now();
    const w      = canvas.width;
    const h      = canvas.height;

    const rawDt = now - lastTimeRef.current;
    const dt    = Math.min(rawDt / 16.67, 2);
    lastTimeRef.current = now;

    if (!gameStartedRef.current && input.active) {
      startTimeRef.current   = now;
      nextSampleRef.current  = now + 250;
      gameStartedRef.current = true;
      setUserPath([]);
      recordPosition();
    }

    const gp          = navigator.getGamepads()[0];
    const r2Trigger   = gp?.buttons[7]?.value ?? 0;
    const l2Trigger   = gp?.buttons[6]?.value ?? 0;
    const accelFactor = input.active ? 1 + r2Trigger * 2 : 1;

    if (l2Trigger > 0.05) {
      // BRAKE
    } else if (input.active) {
      ball.x += input.dx * 5 * accelFactor * dt;
      ball.y += input.dy * 5 * accelFactor * dt;
    }

    // clamp — canvas coords now exactly = visible screen
    const r = ball.size;
    ball.x = Math.max(r, Math.min(w - r, ball.x));
    ball.y = Math.max(r, Math.min(h - r, ball.y));

    const deltaX = ball.x - lastXRef.current;
    lastXRef.current = ball.x;

    if (!endLineRef.current && ball.x > w / 2) endLineRef.current = true;

    if (gameStartedRef.current && now >= nextSampleRef.current) {
      recordPosition();
      nextSampleRef.current = now + 250;
    }

    if (ball.y < h * 0.4 || ball.y > h * 0.7) penaltyRef.current += rawDt / 1000;

    if (endLineRef.current && ball.x >= w * 0.9 && deltaX > 0.5 && !finishedRef.current) {
      finishedRef.current = true;
      finishGame();
    }
  };

  const recordPosition = () => {
    const ball = ballRef.current;
    setUserPath(prev => {
      const pos = [ball.x, ball.y];
      if (!prev.length || prev[prev.length-1][0] !== pos[0] || prev[prev.length-1][1] !== pos[1])
        return [...prev, pos];
      return prev;
    });
  };

  // ================= DRAW =================
  const draw = () => {
    const canvas = canvasRef.current;
    const ctx    = canvas.getContext("2d");
    const w      = canvas.width;
    const h      = canvas.height;

    ctx.clearRect(0, 0, w, h);
    drawZigZag(ctx, w, h, 0.4, 0.6, outerLineCoordsRef);
    drawZigZag(ctx, w, h, 0.5, 0.7, innerLineCoordsRef);

    let relx = 0.1;
    let y1o = 0.4*h, y2o = 0.6*h, y1i = 0.5*h, y2i = 0.7*h;
    let lastOuter = {}, lastInner = {};

    for (let i = 0; i < 4; i++) {
      relx += 0.2;
      lastOuter = { x: relx * w, y: y2o };
      lastInner = { x: relx * w, y: y2i };
      [y1o, y2o] = [y2o, y1o];
      [y1i, y2i] = [y2i, y1i];
      endLineCoordsRef.current = [{ x1: lastInner.x, y1: lastInner.y, x2: lastOuter.x, y2: lastOuter.y }];
    }

    ctx.beginPath();
    ctx.moveTo(lastInner.x, lastInner.y);
    ctx.lineTo(lastOuter.x, lastOuter.y);
    ctx.strokeStyle = "red"; ctx.lineWidth = 2; ctx.stroke();
    ctx.strokeStyle = "black";

    const ball = ballRef.current;
    ctx.beginPath();
    ctx.arc(ball.x, ball.y, ball.size, 0, Math.PI * 2);
    ctx.fillStyle = "blue"; ctx.fill();
  };

  const drawZigZag = (ctx, w, h, y1Ratio, y2Ratio, storeRef) => {
    let relx = 0.1, y1 = y1Ratio * h, y2 = y2Ratio * h;
    const coords = [];
    for (let i = 0; i < 4; i++) {
      const x1 = relx * w; relx += 0.2; const x2 = relx * w;
      coords.push({ x1, y1, x2, y2 });
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
      [y1, y2] = [y2, y1];
    }
    storeRef.current = coords;
  };

  // ================= FINISH =================
  const finishGame = () => {
    const endTime     = Date.now();
    const testTime    = (endTime - startTimeRef.current) / 1000;
    const penaltyTime = penaltyRef.current || 0;
    const totalTime   = testTime + penaltyTime;

    const result = {
      GameName: "Line Path", userPath,
      testTime: Number(testTime.toFixed(2)),
      penaltyTime: Number(penaltyTime.toFixed(2)),
      totalTime: Number(totalTime.toFixed(2)),
      StartBallX: ballRef.current.x, StartBallY: ballRef.current.y,
      innerLineCoordsList: innerLineCoordsRef.current || [],
      outerLineCoordsList: outerLineCoordsRef.current || [],
      endLineCoords: endLineCoordsRef.current || [],
      ballSize: ballRef.current.size
    };

    setFinishMsg({ time: result.totalTime, testTime: result.testTime, penaltyTime: result.penaltyTime, score: result });

    axios.post("/save-handeye", {
      email: localStorage.getItem("user_email"), device: "Gamepad", data: { LinePathData: result }
    }).catch(err => console.error("Save error:", err));
  };

  return (
    <div style={{ overflow: "hidden", width: "100vw", height: "100vh", margin: 0, padding: 0 }}>
      <canvas ref={canvasRef} style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", display: "block", background: "#fff" }} />

      {finishMsg && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999 }}>
          <div style={{ background: "white", padding: "40px 48px", borderRadius: "16px", textAlign: "center", minWidth: "300px", boxShadow: "0 8px 32px rgba(0,0,0,0.3)" }}>
            <h2 style={{ margin: "0 0 8px", fontSize: "24px", color: "#222" }}>Test Complete!</h2>
            <p style={{ color: "#666", marginBottom: "20px", fontSize: "15px" }}>Line Path finished</p>
            <div style={{ background: "#f5f5f5", borderRadius: "10px", padding: "16px", marginBottom: "24px" }}>
              <p style={{ margin: "4px 0", fontSize: "14px", color: "#555" }}>Test time: <b>{finishMsg.testTime}s</b></p>
              <p style={{ margin: "4px 0", fontSize: "14px", color: "#d32f2f" }}>Penalty time: <b>{finishMsg.penaltyTime}s</b></p>
              <p style={{ margin: "12px 0 0", fontSize: "20px", fontWeight: "bold", color: "#222" }}>Total: {finishMsg.time}s</p>
            </div>
            <button onClick={() => { setFinishMsg(null); navigate("/hand-eye/circle", { state: { currentScore: finishMsg.time } }); }}
              style={{ padding: "12px 40px", background: "#4CAF50", color: "white", border: "none", borderRadius: "10px", fontSize: "18px", fontWeight: "bold", cursor: "pointer" }}>
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HandEyeLinePlay;
